/**
 * Einstellungen unter `/settings` — die Standalone-Entsprechung dessen, was im
 * Desktop auf die Settings-App und die Communicator-Einstellungsseite verteilt
 * ist (#504).
 *
 * Ohne Shell gibt es keinen zweiten Ort, an den man Nutzer schicken könnte,
 * deshalb steht hier beides untereinander: erst das Allgemeine (Sprache,
 * Erscheinungsbild), dann die App-Einstellungen des Communicators. Letztere
 * werden nicht nachgebaut, sondern als dieselbe Komponente eingebettet — ohne
 * `onBack`, weil die Überschrift von hier kommt und es keinen Chat gibt, zu dem
 * ein Zurück-Pfeil führen würde.
 *
 * Bewusst nur die Schalter, die ohne Desktop noch etwas bedeuten: Desktop-Icons,
 * Taskleiste und Widgets konfigurieren eine Shell, die es hier nicht gibt.
 */
import { Suspense, lazy } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, MessageSquare, Palette } from 'lucide-react'
import { AppearanceSettings } from '@/components/settings/AppearanceSettings'

// Lazy: die Communicator-Einstellungen ziehen den Soul-Editor mit (Tabs,
// Textarea) — geladen erst, wenn jemand die Einstellungen wirklich öffnet.
const Communicator2Settings = lazy(() =>
  import('@/components/applications/communicator2/components/Communicator2Settings').then(
    (m) => ({ default: m.Communicator2Settings }),
  ),
)

function SectionHeading({
  icon: Icon,
  title,
  hint,
}: {
  icon: typeof Palette
  title: string
  hint?: string
}) {
  return (
    <div>
      <div className="flex items-center gap-2 text-sm font-semibold text-[var(--text-color)]">
        <Icon className="h-4 w-4" />
        {title}
      </div>
      {hint && <p className="mt-1 text-xs text-[var(--text-secondary)]">{hint}</p>}
    </div>
  )
}

export function StandaloneSettingsPage() {
  const { t } = useTranslation()

  return (
    <div className="app-surface app-surface--scrollable flex h-full min-h-0 flex-col overflow-y-auto">
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-8 px-4 py-6">
        <h1 className="text-base font-semibold text-[var(--text-color)]">
          {t('standalone.settings.title')}
        </h1>

        <section className="flex flex-col gap-4">
          <SectionHeading
            icon={Palette}
            title={t('standalone.settings.generalTitle')}
            hint={t('standalone.settingsDescription')}
          />
          <AppearanceSettings />
        </section>

        <section className="flex flex-col gap-6 border-t border-[var(--window-border-color)] pt-6">
          <SectionHeading
            icon={MessageSquare}
            title={t('apps.communicator2')}
            hint={t('standalone.settings.communicatorHint')}
          />
          <Suspense
            fallback={
              <div className="flex items-center gap-2 py-6 text-[var(--text-secondary)]">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            }
          >
            {/* Kein onBack: die Überschrift steht oben, und es gibt keinen Chat,
                zu dem ein Zurück-Pfeil führen würde. */}
            <Communicator2Settings />
          </Suspense>
        </section>
      </div>
    </div>
  )
}
